import { createI18n } from 'vue-i18n'
import { DEFAULT_LOCALE } from './locales'

// Eagerly load English as fallback
import en from './messages/en.json'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MessageSchema = typeof en
type MessageLoaderResult = { default: Record<string, any> }
type MessageLoader = () => Promise<MessageLoaderResult>

export const i18n = createI18n<[MessageSchema], string>({
  legacy: false,              // Use Composition API
  globalInjection: true,      // Enable $t in templates
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en },
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
})

// Lazy load other locales
const messageLoaders: Record<string, MessageLoader> = {
  de: () => import('./messages/de.json'),
  es: () => import('./messages/es.json'),
  'es-419': () => import('./messages/es-419.json'),
  fr: () => import('./messages/fr.json'),
  it: () => import('./messages/it.json'),
  pt: () => import('./messages/pt.json'),
  'pt-BR': () => import('./messages/pt-BR.json'),
  ja: () => import('./messages/ja.json'),
  ko: () => import('./messages/ko.json'),
  zh: () => import('./messages/zh.json'),
  'zh-TW': () => import('./messages/zh-TW.json'),
  ar: () => import('./messages/ar.json'),
  hi: () => import('./messages/hi.json'),
  id: () => import('./messages/id.json'),
  tr: () => import('./messages/tr.json'),
  ru: () => import('./messages/ru.json'),
  nl: () => import('./messages/nl.json'),
  pl: () => import('./messages/pl.json'),
  sv: () => import('./messages/sv.json'),
  th: () => import('./messages/th.json'),
}

const loadedLocales = new Set<string>(['en'])

export async function loadLocaleMessages(locale: string): Promise<void> {
  if (loadedLocales.has(locale)) return

  const loader = messageLoaders[locale]
  if (!loader) {
    console.warn(`[i18n] No message loader for locale: ${locale}`)
    return
  }

  try {
    const messages = await loader()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    i18n.global.setLocaleMessage(locale, messages.default as any)
    loadedLocales.add(locale)
  } catch (error) {
    console.error(`[i18n] Failed to load messages for ${locale}:`, error)
  }
}

export async function setI18nLocale(locale: string): Promise<void> {
  await loadLocaleMessages(locale)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(i18n.global.locale as any).value = locale
}
